import React, { useState, useEffect, useContext } from 'react';
import { useCuringEstimate } from '../../api/curing';
import CuringControl from './CuringControl';
import DroughtFactorControl from './DroughtFactorControl';
import DroughtIndexControl from './DroughtIndexControl';
import { Box, Typography, Slider, Button, FormControl, InputLabel, Select, MenuItem, SelectChangeEvent, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Snackbar, Alert } from '@mui/material';
import {
  Thermostat as ThermostatIcon,
  Opacity as OpacityIcon,
  Air as AirIcon,
  PlayArrow as PlayArrowIcon,
  BookmarkAdd as BookmarkAddIcon,
  ArrowBack as ArrowBackIcon,
  Tune as TuneIcon,
} from '@mui/icons-material';
import WeatherChart from '../WeatherChart';
import WeatherPointEditor, { WeatherPointValues } from './WeatherPointEditor';
import SpottingModeSelector from './SpottingModeSelector';
import { SpottingMode } from '../../types/spotting';
import { useWeatherStatistics } from '../../api/weatherHistory';
import { useWeather, ApiWeather, useCalculateFFDI } from '../../api/weather';
import { useSimulation, SimulationParams } from '../../contexts/SimulationContext';
import { useTimezone } from '../../contexts/TimezoneContext';
import { IgnitionRegimeContext } from '../../contexts/IgnitionRegimeContext';
import { useAuth } from '../../contexts/AuthContext';
import dayjs from 'dayjs';
import { transposeWeatherTimestamps } from '../../utils/datetime';
import { useCreateWeatherPreset } from '../../api/weatherPresets';
import { getDangerCategory } from '../../utils/fireDanger';
import WindCompass, { cardinalToDegrees } from './WindCompass';
import { useUnitSystem } from '../../hooks/useUnitSystem';
import {
  formatSpeed,
  formatTemperature,
  toDisplaySpeed,
  toDisplayTemperature,
  toMetricSpeed,
  toMetricTemperature,
} from '../../utils/units';

interface PresetValues {
  temperature?: number;
  humidity?: number;
  windSpeed?: number;
  windDirection?: string;
  droughtFactor?: number;
  droughtIndex?: number | null;
  curing?: number;
  spottingMode?: SpottingMode;
  weatherOverrides?: Record<string, WeatherPointValues>;
}

interface CustomBuildViewProps {
  projectId: string;
  onGenerate?: (params?: SimulationParams) => void;
  onClose?: () => void;
  presetValues?: PresetValues;
  presetName?: string;
  weatherDate: dayjs.Dayjs;
  projectDate: dayjs.Dayjs;
}

const CustomBuildView: React.FC<CustomBuildViewProps> = ({ projectId, onGenerate, onClose, presetValues, presetName: initialPresetName, weatherDate, projectDate }) => {
  const unitSystem = useUnitSystem();
  const { simulationParams, setSimulationParams } = useSimulation();
  const { selectedTimezone } = useTimezone();
  const { selectedRegime } = useContext(IgnitionRegimeContext);
  const { user } = useAuth();

  // Check if user has longrange_simulation capability
  const hasLongRangeCapability = user?.is_staff || user?.capabilities?.some(
    (cap) => cap.codename === 'longrange_simulation'
  );
  const maxDuration = hasLongRangeCapability ? 336 : 48;

  const formatOrdinal = (value: number) => {
    const v = Math.round(value);
    const mod100 = v % 100;
    if (mod100 >= 11 && mod100 <= 13) return `${v}th`;

    switch (v % 10) {
      case 1:
        return `${v}st`;
      case 2:
        return `${v}nd`;
      case 3:
        return `${v}rd`;
      default:
        return `${v}th`;
    }
  };
  
  const { data: stats, isLoading } = useWeatherStatistics(projectId);


  const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

  const [temperature, setTemperature] = useState(presetValues?.temperature ?? 0);
  const [humidity, setHumidity] = useState(presetValues?.humidity ?? 0);
  const [windSpeed, setWindSpeed] = useState(presetValues?.windSpeed ?? 0);
  const [windDirectionDeg, setWindDirectionDeg] = useState(
    presetValues?.windDirection ? cardinalToDegrees(presetValues.windDirection) : 315
  );
  const [droughtFactor, setDroughtFactor] = useState(presetValues?.droughtFactor ?? 0);
  const [curing, setCuring] = useState(presetValues?.curing ?? 100);
  const [droughtIndex, setDroughtIndex] = useState<number | null>(presetValues?.droughtIndex ?? null);
  // Whether the satellite reading is in use, or the user has taken over.
  const [usingCuringEstimate, setUsingCuringEstimate] = useState(!presetValues?.curing);

  // Custom weather is a what-if on real ground, so curing still comes from
  // the project's satellite reading unless the user overrides it.
  const { data: curingData } = useCuringEstimate(projectId, projectDate.format('YYYY-MM-DD'));
  const curingForDate = curingData?.available ? curingData.for_date : undefined;

  useEffect(() => {
    if (!curingForDate || presetValues?.curing !== undefined) return;
    if (curingForDate.within_window && curingForDate.curing !== null) {
      if (usingCuringEstimate) setCuring(curingForDate.curing);
    } else {
      setUsingCuringEstimate(false);
      if (curingForDate.suggested !== null && curingForDate.suggested !== undefined) {
        setCuring(curingForDate.suggested);
      }
    }
  }, [curingForDate, usingCuringEstimate, presetValues?.curing]);
  const [spottingMode, setSpottingMode] = useState<SpottingMode>(
    presetValues?.spottingMode ?? simulationParams?.spottingMode ?? 'realistic'
  );
  const [startHour, setStartHour] = useState(10);
  const [duration, setDuration] = useState(8);
  const [isGenerating, setIsGenerating] = useState(false);
  const [step, setStep] = useState<'configure' | 'review'>('configure');

  const [weatherOverrides, setWeatherOverrides] = useState<Record<string, WeatherPointValues>>({});
  const [selectedPoint, setSelectedPoint] = useState<{ timestamp: string; values: WeatherPointValues; anchorEl: Element } | null>(null);

  const [presetDialogOpen, setPresetDialogOpen] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [presetSnackbar, setPresetSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
  const createPresetMutation = useCreateWeatherPreset();
  const [fireDangerRating, setFireDangerRating] = useState<number>(0);
  const [dangerCategory, setDangerCategory] = useState({ label: 'NO RATING', color: '#9e9e9e' });

  const calculateFFDIMutation = useCalculateFFDI();

  const startDateTime = dayjs
    .tz(weatherDate.format('YYYY-MM-DD'), selectedTimezone)
    .hour(startHour)
    .minute(0)
    .second(0)
    .millisecond(0);

  const { weather: apiWeather }: ApiWeather = useWeather(
    'DEFINE_CUSTOM_WEATHER',
    projectId,
    startDateTime.format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
    duration,
    temperature - 5,
    temperature + 5,
    windSpeed,
    Math.max(0, humidity - 10),
    Math.min(100, humidity + 10),
    windDirectionDeg,
    true
  );

  const handleSimulate = async () => {
    if (!simulationParams) return;

    setIsGenerating(true);

    const simulationTime = dayjs(projectDate)
      .hour(startHour)
      .minute(0)
      .second(0)
      .millisecond(0);

    const simulationTimeTz = simulationTime.tz(selectedTimezone, true);

    // Merge per-point overrides into base weather before transposing
    const base = apiWeather?.data?.interval_weather;
    type WeatherEntry = { wind_speed: number; wind_direction: number; temperature: number; relative_humidity: number };
    const merged = base ? Object.fromEntries(
      Object.entries(base).map(([ts, vals]) => {
        const ov = weatherOverrides[ts];
        return ov ? [ts, { ...(vals as WeatherEntry), ...ov }] : [ts, vals];
      })
    ) : base;

    const transposedWeather = transposeWeatherTimestamps(
      merged,
      weatherDate,
      projectDate,
    );

    const nextParams: SimulationParams = {
      ...simulationParams,
      simulationType: 'DEFINE_CUSTOM_WEATHER',
      droughtFactor: droughtFactor,
      droughtIndex,
      curing: usingCuringEstimate && curingForDate?.within_window ? 'auto' : curing,
      interval: (duration * 60) / 240,
      duration: duration,
      startTime: simulationTimeTz.format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
      timezone: selectedTimezone,
      weather: transposedWeather || {},
      fireSpreadModel: 'New',
      spottingMode,
      ignitionRegimeId: selectedRegime?.id || null,
      weatherLabel: initialPresetName ?? 'Custom weather',
      // Custom weather is uniform by construction: never inherit a lattice
      // left in context by an earlier forecast run.
      weather_grid: undefined,
    };

    setSimulationParams(nextParams);

    // Pass the freshly-built params directly so the generated sim uses the
    // conditions just selected rather than racing the state update above.
    await onGenerate?.(nextParams);
    setIsGenerating(false);
    onClose?.();
  };

  const [tempPercentile, setTempPercentile] = useState('50th');
  const [humidityPercentile, setHumidityPercentile] = useState('50th');
  const [windPercentile, setWindPercentile] = useState('50th');

  const [hasInitializedFromStats, setHasInitializedFromStats] = useState(false);

  useEffect(() => {
    setHasInitializedFromStats(false);
  }, [projectId]);

  useEffect(() => {
    if (hasInitializedFromStats) return;
    if (!stats) return;
    if (presetValues) return;

    setTemperature(Math.round(clamp(stats.temperature.mean, stats.temperature.min, stats.temperature.max)));
    setHumidity(Math.round(clamp(stats.relative_humidity.mean, stats.relative_humidity.min, stats.relative_humidity.max)));
    setWindSpeed(Math.round(clamp(stats.wind_speed.mean, 0, stats.wind_speed.max)));
    setWindDirectionDeg(cardinalToDegrees(stats.wind_speed.dominant_direction || 'NW'));
    setDroughtFactor(Math.round(clamp(stats.drought_factor.mean, stats.drought_factor.min, stats.drought_factor.max)));
    setHasInitializedFromStats(true);
  }, [hasInitializedFromStats, presetValues, stats]);

  // Custom weather is a what-if on real ground, so the drought index starts
  // from the latest value the project's weather history holds (a preset that
  // carries its own value wins).
  useEffect(() => {
    if (droughtIndex !== null || presetValues?.droughtIndex != null) return;
    if (stats?.kbdi?.latest != null) setDroughtIndex(stats.kbdi.latest);
  }, [stats, droughtIndex, presetValues?.droughtIndex]);

  // Function to fetch FFDI from backend
  const fetchFFDI = React.useCallback((temp?: number, hum?: number, wind?: number, df?: number) => {
    const t = temp ?? temperature;
    const h = hum ?? humidity;
    const w = wind ?? windSpeed;
    const d = df ?? droughtFactor;
    
    if (t > 0 && h > 0 && d > 0) {
      calculateFFDIMutation.mutate(
        { temperature: t, humidity: h, windSpeed: w, droughtFactor: d },
        {
          onSuccess: (data) => {
            setFireDangerRating(data.ffdi);
            setDangerCategory(getDangerCategory(data.ffdi));
          },
        }
      );
    }
  }, [temperature, humidity, windSpeed, droughtFactor, calculateFFDIMutation]);

  // Update state when preset values change
  useEffect(() => {
    if (presetValues) {
      const newTemp = presetValues.temperature ?? temperature;
      const newHumidity = presetValues.humidity ?? humidity;
      const newWindSpeed = presetValues.windSpeed ?? windSpeed;
      const newDroughtFactor = presetValues.droughtFactor ?? droughtFactor;
      
      if (presetValues.temperature !== undefined) setTemperature(presetValues.temperature);
      if (presetValues.humidity !== undefined) setHumidity(presetValues.humidity);
      if (presetValues.windSpeed !== undefined) setWindSpeed(presetValues.windSpeed);
      if (presetValues.windDirection !== undefined) setWindDirectionDeg(cardinalToDegrees(presetValues.windDirection));
      if (presetValues.droughtFactor !== undefined) setDroughtFactor(presetValues.droughtFactor);
      if (presetValues.droughtIndex !== undefined) setDroughtIndex(presetValues.droughtIndex);
      if (presetValues.curing !== undefined) setCuring(presetValues.curing);
      if (presetValues.spottingMode !== undefined) setSpottingMode(presetValues.spottingMode);
      if (presetValues.weatherOverrides !== undefined) {
        setWeatherOverrides(presetValues.weatherOverrides);
        setStep('review');
      }
      
      // Fetch FFDI with the new preset values directly
      fetchFFDI(newTemp, newHumidity, newWindSpeed, newDroughtFactor);
    }
  }, [presetValues]);

  // Fetch FFDI on initial load when stats are available
  useEffect(() => {
    if (hasInitializedFromStats && temperature > 0 && humidity > 0 && droughtFactor > 0) {
      fetchFFDI();
    }
  }, [hasInitializedFromStats]);

  const displayTemperature = toDisplayTemperature(temperature, unitSystem);
  const displayWindSpeed = toDisplaySpeed(windSpeed, unitSystem);

  const temperatureSliderMin = toDisplayTemperature(-10, unitSystem);
  const temperatureSliderMax = toDisplayTemperature(58, unitSystem);
  const windSpeedSliderMin = toDisplaySpeed(0, unitSystem);
  const windSpeedSliderMax = toDisplaySpeed(100, unitSystem);


  // Update percentiles when sliders change
  useEffect(() => {
    if (!stats?.temperature || !stats?.relative_humidity || !stats?.wind_speed) return;
    
    // Calculate approximate percentiles based on slider values
    const tempRange = stats.temperature.max - stats.temperature.min;
    const tempPct = ((temperature - stats.temperature.min) / tempRange) * 100;
    setTempPercentile(formatOrdinal(tempPct));

    const humidityRange = stats.relative_humidity.max - stats.relative_humidity.min;
    const humidityPct = ((humidity - stats.relative_humidity.min) / humidityRange) * 100;
    setHumidityPercentile(formatOrdinal(100 - humidityPct)); // Inverted for humidity

    const windPct = (windSpeed / stats.wind_speed.max) * 100;
    setWindPercentile(formatOrdinal(windPct));
  }, [temperature, humidity, windSpeed, stats]);

  const handleSavePreset = async () => {
    const name = presetName.trim();
    if (!name) return;
    try {
      await createPresetMutation.mutateAsync({
        name,
        preset_type: 'custom',
        params: {
          temperature,
          humidity,
          wind_speed: windSpeed,
          wind_direction_deg: windDirectionDeg,
          drought_factor: droughtFactor,
          drought_index: droughtIndex,
          curing,
          spotting_mode: spottingMode,
          weather_overrides: Object.keys(weatherOverrides).length > 0 ? weatherOverrides : undefined,
        },
      });
      setPresetDialogOpen(false);
      setPresetSnackbar({ open: true, message: `Preset "${name}" saved!`, severity: 'success' });
    } catch {
      setPresetSnackbar({ open: true, message: 'Failed to save preset', severity: 'error' });
    }
  };

  const mergedWeather = (() => {
    const base = apiWeather?.data?.interval_weather;
    if (!base) return base;
    type WeatherEntry = { wind_speed: number; wind_direction: number; temperature: number; relative_humidity: number };
    return Object.fromEntries(
      Object.entries(base).map(([ts, vals]) => {
        const ov = weatherOverrides[ts];
        return ov ? [ts, { ...(vals as WeatherEntry), ...ov }] : [ts, vals];
      })
    );
  })();

  const handlePointClick = (timestamp: string, raw: { wind_speed: number; wind_direction: number; temperature: number; relative_humidity: number }, event: MouseEvent) => {
    const current = weatherOverrides[timestamp] ?? {
      temperature: raw.temperature,
      relative_humidity: raw.relative_humidity,
      wind_speed: raw.wind_speed,
      wind_direction: raw.wind_direction,
    };
    setSelectedPoint({ timestamp, values: current, anchorEl: event.target as Element });
  };

  const handleApplyOverride = (timestamp: string, values: WeatherPointValues) => {
    setWeatherOverrides((prev) => ({ ...prev, [timestamp]: values }));
    setSelectedPoint((prev) => prev ? { ...prev, timestamp, values } : null);
  };

  const handleResetOverride = (timestamp: string) => {
    setWeatherOverrides((prev) => {
      const next = { ...prev };
      delete next[timestamp];
      return next;
    });
    const raw = apiWeather?.data?.interval_weather?.[timestamp];
    if (raw) {
      setSelectedPoint({
        timestamp,
        values: {
          temperature: raw.temperature,
          relative_humidity: raw.relative_humidity,
          wind_speed: raw.wind_speed,
          wind_direction: raw.wind_direction,
        },
        anchorEl: selectedPoint?.anchorEl ?? document.body,
      });
    } else {
      setSelectedPoint(null);
    }
  };

  const handleEnterReview = () => {
    setWeatherOverrides({});
    setSelectedPoint(null);
    setStep('review');
  };

  if (isLoading || !stats) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>Loading weather statistics...</Typography>
      </Box>
    );
  }

  if (step === 'review') {
    const hasWeather = !!apiWeather?.data?.interval_weather;
    return (
      <Box>
        {/* Review header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => setStep('configure')}
            sx={{ color: 'rgba(255,255,255,0.6)', '&:hover': { color: '#fff' } }}
          >
            Back
          </Button>
          <Box>
            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 600, mb: 0.5 }}>
              Review & Adjust
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
              Click any point on the chart to fine-tune conditions for that hour.
            </Typography>
          </Box>
        </Box>

        {/* FFDI summary chip */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 2,
            bgcolor: `${dangerCategory.color}18`,
            border: `1px solid ${dangerCategory.color}60`,
            borderRadius: 2,
            px: 2.5,
            py: 1,
            mb: 3,
          }}
        >
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
            Base FFDI
          </Typography>
          <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>
            {fireDangerRating.toFixed(0)}
          </Typography>
          <Typography variant="body2" sx={{ color: dangerCategory.color, fontWeight: 700 }}>
            {dangerCategory.label}
          </Typography>
        </Box>

        {/* Chart */}
        <Box sx={{ bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 2, p: 3, mb: 3 }}>
          {hasWeather ? (
            <>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block', mb: 1 }}>
                Click any point to edit weather conditions for that hour
              </Typography>
              <WeatherChart
                data={mergedWeather ?? apiWeather!.data.interval_weather}
                onPointClick={handlePointClick}
                overriddenTimestamps={new Set(Object.keys(weatherOverrides))}
                selectedTimestamp={selectedPoint?.timestamp ?? null}
              />
            </>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
              <CircularProgress sx={{ color: '#ff6b35' }} />
            </Box>
          )}
        </Box>

        {/* Action buttons */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            size="large"
            startIcon={<BookmarkAddIcon />}
            onClick={() => { setPresetName(''); setPresetDialogOpen(true); }}
            sx={{
              borderColor: 'rgba(255,255,255,0.3)',
              color: 'rgba(255,255,255,0.7)',
              py: 2,
              '&:hover': { borderColor: '#fff', color: '#fff' },
            }}
          >
            Save Preset
          </Button>
          <Button
            variant="contained"
            size="large"
            sx={{
              flex: 1,
              bgcolor: '#ff6b35',
              py: 2,
              fontSize: '1.1rem',
              fontWeight: 600,
              '&:hover': { bgcolor: '#ff7f50' },
            }}
            disabled={isGenerating || !hasWeather}
            startIcon={isGenerating ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <PlayArrowIcon />}
            onClick={handleSimulate}
          >
            {isGenerating ? 'GENERATING...' : 'GENERATE SIMULATION'}
          </Button>
        </Box>

        {/* Preset dialog */}
        <Dialog open={presetDialogOpen} onClose={() => setPresetDialogOpen(false)} PaperProps={{ sx: { bgcolor: '#1a1d2e', color: '#fff' } }}>
          <DialogTitle sx={{ color: '#fff' }}>Save as Preset</DialogTitle>
          <DialogContent sx={{ pt: 1 }}>
            <TextField
              autoFocus
              margin="dense"
              label="Preset name"
              fullWidth
              size="small"
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSavePreset(); }}
              sx={{ '& .MuiOutlinedInput-root': { color: '#fff', '& fieldset': { borderColor: 'rgba(255,255,255,0.23)' }, '&.Mui-focused fieldset': { borderColor: '#ff6b35' } }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)', '&.Mui-focused': { color: '#ff6b35' } } }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPresetDialogOpen(false)} sx={{ color: 'rgba(255,255,255,0.6)' }}>Cancel</Button>
            <Button onClick={handleSavePreset} variant="contained" disabled={!presetName.trim() || createPresetMutation.isPending} sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#ff7f50' } }}>
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={presetSnackbar.open} autoHideDuration={3000} onClose={() => setPresetSnackbar(p => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert severity={presetSnackbar.severity} onClose={() => setPresetSnackbar(p => ({ ...p, open: false }))}>
            {presetSnackbar.message}
          </Alert>
        </Snackbar>

        {selectedPoint && (
          <WeatherPointEditor
            anchorEl={selectedPoint.anchorEl}
            timestamp={selectedPoint.timestamp}
            values={selectedPoint.values}
            isOverridden={!!weatherOverrides[selectedPoint.timestamp]}
            onApply={handleApplyOverride}
            onReset={handleResetOverride}
            onClose={() => setSelectedPoint(null)}
          />
        )}
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ color: '#fff', fontWeight: 600, mb: 1 }}>
          Weather Mixer
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
          Mix your own weather scenario. The system will calculate statistical significance based on local
          history.
        </Typography>
      </Box>

      {/* Sliders */}
      <Box sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)', borderRadius: 2, p: 4, mb: 3 }}>
        {/* Temperature */}
        <Box sx={{ mb: 5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <ThermostatIcon sx={{ color: '#ff6b35' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', textTransform: 'uppercase', fontSize: '0.75rem' }}>
                Temperature
              </Typography>
              <Typography variant="h4" sx={{ color: '#fff', fontWeight: 600 }}>
                {formatTemperature(temperature, unitSystem)}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" sx={{ color: '#ff6b35', fontWeight: 600 }}>
                {tempPercentile} Percentile
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block' }}>
                Historical Significance
              </Typography>
            </Box>
          </Box>
          <Slider
            value={displayTemperature}
            onChange={(_, value) => setTemperature(toMetricTemperature(value as number, unitSystem))}
            onChangeCommitted={() => fetchFFDI()}
            min={temperatureSliderMin}
            max={temperatureSliderMax}
            step={1}
            sx={{
              color: '#ff6b35',
              '& .MuiSlider-thumb': {
                width: 20,
                height: 20,
              },
              '& .MuiSlider-track': {
                height: 6,
              },
              '& .MuiSlider-rail': {
                height: 6,
                bgcolor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>{formatTemperature(-10, unitSystem)}</Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>{formatTemperature(25, unitSystem)}</Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>{formatTemperature(58, unitSystem)}</Typography>
          </Box>
        </Box>

        {/* Relative Humidity */}
        <Box sx={{ mb: 5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <OpacityIcon sx={{ color: '#64b5f6' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', textTransform: 'uppercase', fontSize: '0.75rem' }}>
                Relative Humidity
              </Typography>
              <Typography variant="h4" sx={{ color: '#fff', fontWeight: 600 }}>
                {humidity}%
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" sx={{ color: '#64b5f6', fontWeight: 600 }}>
                {humidityPercentile} Percentile
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block' }}>
                Historical Significance
              </Typography>
            </Box>
          </Box>
          <Slider
            value={humidity}
            onChange={(_, value) => setHumidity(value as number)}
            onChangeCommitted={() => fetchFFDI()}
            min={0}
            max={100}
            step={1}
            sx={{
              color: '#64b5f6',
              '& .MuiSlider-thumb': {
                width: 20,
                height: 20,
              },
              '& .MuiSlider-track': {
                height: 6,
              },
              '& .MuiSlider-rail': {
                height: 6,
                bgcolor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>0% (Dry)</Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>50%</Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>100% (Wet)</Typography>
          </Box>
        </Box>

        {/* Wind Speed */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <AirIcon sx={{ color: '#9c27b0' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', textTransform: 'uppercase', fontSize: '0.75rem' }}>
                Wind Speed
              </Typography>
              <Typography variant="h4" sx={{ color: '#fff', fontWeight: 600 }}>
                {formatSpeed(windSpeed, unitSystem)}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" sx={{ color: '#9c27b0', fontWeight: 600 }}>
                {windPercentile} Percentile
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block' }}>
                Historical Significance
              </Typography>
            </Box>
          </Box>
          <Slider
            value={displayWindSpeed}
            onChange={(_, value) => setWindSpeed(toMetricSpeed(value as number, unitSystem))}
            onChangeCommitted={() => fetchFFDI()}
            min={windSpeedSliderMin}
            max={windSpeedSliderMax}
            step={1}
            sx={{
              color: '#9c27b0',
              '& .MuiSlider-thumb': {
                width: 20,
                height: 20,
              },
              '& .MuiSlider-track': {
                height: 6,
              },
              '& .MuiSlider-rail': {
                height: 6,
                bgcolor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>{toDisplaySpeed(0, unitSystem)}</Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>{toDisplaySpeed(50, unitSystem)}</Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>{toDisplaySpeed(100, unitSystem)}+</Typography>
          </Box>
        </Box>

        {/* Wind Direction */}
        <Box sx={{ mb: 5 }}>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', textTransform: 'uppercase', fontSize: '0.75rem', mb: 2 }}>
            Wind Direction
          </Typography>
          <WindCompass degrees={windDirectionDeg} onChange={setWindDirectionDeg} size={200} />
        </Box>

        {/* Drought Factor, Curing and Drought Index side by side */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, mb: 5 }}>
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <DroughtFactorControl
              value={droughtFactor}
              onChange={setDroughtFactor}
              onChangeCommitted={() => fetchFFDI()}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <CuringControl
              value={curing}
              onChange={(next) => { setUsingCuringEstimate(false); setCuring(next); }}
              forDate={curingForDate}
              usingEstimate={usingCuringEstimate}
              onUseEstimate={(use) => {
                setUsingCuringEstimate(use);
                if (use && curingForDate?.curing !== null && curingForDate?.curing !== undefined) {
                  setCuring(curingForDate.curing);
                }
              }}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <DroughtIndexControl
              value={droughtIndex}
              onChange={setDroughtIndex}
              unitSystem={unitSystem}
              source={stats?.kbdi?.date
                ? `Latest value for this project, as of ${stats.kbdi.date}. Drag to test a drier or wetter landscape.`
                : null}
            />
          </Box>
        </Box>

        {/* Spotting */}
        <Box sx={{ mt: 4 }}>
          <SpottingModeSelector value={spottingMode} onChange={setSpottingMode} />
        </Box>
      </Box>

      {/* Time Selection */}
      <Box sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)', borderRadius: 2, p: 4, mb: 3 }}>
        <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, mb: 3 }}>
          Simulation Time
        </Typography>
        <Box sx={{ display: 'flex', gap: 3 }}>
          <Box sx={{ flex: 1 }}>
            <FormControl fullWidth>
              <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Start Time</InputLabel>
              <Select
                value={startHour}
                onChange={(e: SelectChangeEvent<number>) => setStartHour(Number(e.target.value))}
                label="Start Time"
                sx={{
                  color: '#fff',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#ff6b35',
                  },
                  '& .MuiSvgIcon-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                }}
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <MenuItem key={i} value={i}>
                    {i.toString().padStart(2, '0')}:00
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ flex: 1 }}>
            <FormControl fullWidth>
              <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Duration</InputLabel>
              <Select
                value={duration}
                onChange={(e: SelectChangeEvent<number>) => setDuration(Number(e.target.value))}
                label="Duration"
                sx={{
                  color: '#fff',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#ff6b35',
                  },
                  '& .MuiSvgIcon-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                }}
              >
                {Array.from(
                  { length: Math.floor((maxDuration - 8) / 2) + 1 },
                  (_, i) => 8 + i * 2
                ).map((hours) => (
                  <MenuItem key={hours} value={hours}>
                    {hours} hrs {hours > 48 ? `(${Math.floor(hours / 24)}d ${hours % 24}h)` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>

      {/* Resulting Danger Index */}
      <Box
        sx={{
          bgcolor: 'rgba(0, 0, 0, 0.3)',
          border: `2px solid ${dangerCategory.color}`,
          borderRadius: 2,
          p: 3,
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', textTransform: 'uppercase', display: 'block', mb: 1 }}>
              Resulting Danger Index
            </Typography>
            <Typography variant="h2" sx={{ color: '#fff', fontWeight: 700 }}>
              {fireDangerRating.toFixed(0)}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography
              variant="h5"
              sx={{
                color: dangerCategory.color,
                fontWeight: 700,
                bgcolor: `${dangerCategory.color}20`,
                px: 2,
                py: 1,
                borderRadius: 1,
              }}
            >
              {dangerCategory.label}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block', mt: 1 }}>
              This custom scenario is ready for simulation.
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          size="large"
          sx={{
            flex: 1,
            bgcolor: '#ff6b35',
            py: 2,
            fontSize: '1.1rem',
            fontWeight: 600,
            '&:hover': { bgcolor: '#ff7f50' },
          }}
          startIcon={<TuneIcon />}
          onClick={handleEnterReview}
        >
          BUILD
        </Button>
      </Box>

      {/* Snackbar feedback */}
      <Snackbar open={presetSnackbar.open} autoHideDuration={3000} onClose={() => setPresetSnackbar(p => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={presetSnackbar.severity} onClose={() => setPresetSnackbar(p => ({ ...p, open: false }))}>
          {presetSnackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomBuildView;
